import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Phone } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import Field, { inputCls } from '@/components/Field';
import { Reveal, Stagger, StaggerItem } from '@/components/Reveal';
import CTABand from '@/components/CTABand';
import { useLanguage } from '@/context/LanguageContext';
import { API } from '@/lib/api';

const EMPTY = { name: '', phone: '', city: '', vehicles: '', date: '', notes: '' };

export default function Maintenance() {
  const { t } = useLanguage();
  const [answers, setAnswers] = useState([null, null, null]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const timeline = t('maintenance.timeline');
  const hardware = t('maintenance.hardware.items');
  const diag = t('maintenance.diag');
  const questions = [diag.q1, diag.q2, diag.q3];

  const answered = answers.every((a) => a !== null);
  const needsFix = answers.some((a) => a > 0);

  const setA = (qi, oi) => setAnswers((a) => a.map((v, i) => (i === qi ? oi : v)));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error(t('common.required')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/maintenance`, {
        name: form.name, phone: form.phone, city: form.city,
        vehicles: form.vehicles, preferred_date: form.date, notes: form.notes,
      });
      toast.success(t('maintenance.form.success'));
      setForm(EMPTY);
    } catch { toast.error(t('common.error')); } finally { setLoading(false); }
  };

  return (
    <div className="pt-28" data-testid="maintenance-page">
      <section className="mx-auto max-w-7xl px-5 pt-8 lg:px-8">
        <SectionHeading eyebrow="maintenance.eyebrow" title="maintenance.title" sub="maintenance.sub" align="center" />
      </section>

      {/* Timeline */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8" data-testid="install-timeline">
        <Stagger className="relative space-y-8" gap={0.12}>
          <span className="absolute bottom-4 start-[19px] top-4 border-s-2 border-dashed border-brand-line" />
          {timeline.map((step, i) => (
            <StaggerItem key={step.t}>
              <div className="relative flex gap-5">
                <span className="relative z-10 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-blue font-mono text-sm font-bold text-white" data-testid={`timeline-step-${i}`}>{i + 1}</span>
                <div className="rounded-2xl border border-brand-line bg-white p-5 flex-1 card-hover">
                  <h3 className="font-display text-lg font-bold text-brand-ink">{step.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-brand-steel">{step.d}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Hardware */}
      <section className="border-y border-brand-line bg-white py-20" data-testid="hardware-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] border border-brand-line">
              <img src={t('maintenance.img')} alt="WiyakGPS installation" className="h-80 w-full object-cover" data-testid="hardware-image" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 to-transparent" />
              <span className="absolute bottom-4 start-4 rounded-full bg-white/90 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-brand-ink glass">IP67 · 4G-LTE · 10S FIX</span>
            </div>
          </Reveal>
          <div>
            <Reveal><h2 className="font-display text-2xl font-extrabold tracking-tight text-brand-ink sm:text-3xl">{t('maintenance.hardware.title')}</h2></Reveal>
            <Stagger className="mt-6 grid gap-3 sm:grid-cols-2">
              {hardware.map((h) => (
                <StaggerItem key={h.name}>
                  <div className="card-hover rounded-2xl border border-brand-line bg-brand-paper p-5" data-testid={`hardware-card-${h.name}`}>
                    <h3 className="font-display text-base font-bold text-brand-ink">{h.name}</h3>
                    <p className="mt-1.5 font-mono text-[11px] uppercase tracking-wide text-brand-steel">{h.spec}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* Diagnostic */}
      <section className="mx-auto max-w-3xl px-5 py-20 lg:px-8" data-testid="diag-section">
        <Reveal>
          <div className="rounded-[2rem] border border-brand-line bg-white p-8 shadow-[0_40px_80px_-40px_rgba(15,23,42,0.2)]">
            <h2 className="font-display text-2xl font-extrabold text-brand-ink">{diag.title}</h2>
            <p className="mt-2 text-sm text-brand-steel">{diag.sub}</p>
            <div className="mt-8 space-y-6">
              {questions.map((q, qi) => (
                <div key={qi} data-testid={`diag-question-${qi}`}>
                  <p className="text-sm font-bold text-brand-ink">{qi + 1}. {q.q}</p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {q.opts.map((opt, oi) => (
                      <button
                        key={opt}
                        data-testid={`diag-option-${qi}-${oi}`}
                        onClick={() => setA(qi, oi)}
                        className={`rounded-full border px-4 py-2 text-xs font-bold transition-all duration-300 ${answers[qi] === oi ? 'border-brand-blue bg-brand-blue text-white' : 'border-brand-line bg-white text-brand-steel hover:border-brand-blue hover:text-brand-blue'}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {answered && (
              <div className={`mt-8 flex items-center gap-3 rounded-2xl px-5 py-4 ${needsFix ? 'bg-brand-amber/10 text-amber-700' : 'bg-brand-emerald/10 text-emerald-700'}`} data-testid="diag-result">
                {needsFix ? <AlertTriangle className="h-5 w-5 shrink-0 text-brand-amber" /> : <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-emerald" />}
                <p className="text-sm font-bold">{needsFix ? diag.resultFix : diag.resultOk}</p>
              </div>
            )}
            <div className="mt-6 flex gap-3">
              <button
                data-testid="diag-run-btn"
                onClick={() => setAnswers([null, null, null])}
                className="rounded-full border border-brand-line px-5 py-2.5 text-sm font-bold text-brand-steel transition-colors hover:text-brand-blue"
              >
                {diag.again}
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Booking form */}
      <section className="border-t border-brand-line bg-white py-20" data-testid="maintenance-form-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeading eyebrow="maintenance.eyebrow" title="maintenance.form.title" align="start" />
            <Reveal delay={0.15}>
              <a href={`tel:${t('settings.phone').replace(/\s/g, '')}`} className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-brand-line bg-brand-paper px-5 py-4 transition-colors hover:border-brand-blue" data-testid="maintenance-phone-chip">
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-blue text-white"><Phone className="h-4 w-4" /></span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-brand-dim">{t('contact.channels.phone')}</span>
                  <span className="block font-mono text-sm font-bold text-brand-ink" dir="ltr">{t('settings.phone')}</span>
                </span>
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-[2rem] border border-brand-line bg-brand-paper p-7" data-testid="maintenance-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('maintenance.form.name')} testid="maintenance-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Field label={t('maintenance.form.phone')} testid="maintenance-phone-input" dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Field label={t('maintenance.form.city')} testid="maintenance-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                <Field label={t('maintenance.form.vehicles')} testid="maintenance-vehicles-input" type="number" min="1" value={form.vehicles} onChange={(e) => setForm({ ...form, vehicles: e.target.value })} />
                <div className="sm:col-span-2">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold text-brand-steel">{t('maintenance.form.date')}</span>
                    <input type="date" data-testid="maintenance-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-bold text-brand-steel">{t('maintenance.form.notes')}</span>
                    <textarea rows="3" data-testid="maintenance-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
                  </label>
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="maintenance-submit-btn" className="mt-6 w-full rounded-full bg-brand-blue py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-blueHover disabled:opacity-60">
                {loading ? t('common.submitting') : t('maintenance.form.submit')}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
